import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import { getGoalBalance } from '@common/react/goals';
import { sum } from '@common/util/arrays';
import { createContext, useContext } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { DashboardChartType } from './helpers';

interface DashboardContext {
    chartType: DashboardChartType;
    setChartType: (chartType: DashboardChartType) => void;
}

const Ctx = createContext<DashboardContext>({
    chartType: 'in-plenty',
    setChartType: () => {},
});

export const DashboardProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const { fes } = useFinancialEntitiesContext();

    const [chartType, setChartType] =
        useLocalStorageState<DashboardChartType | null>(
            'dashboard-main-chart-type',
            {
                defaultValue: null,
            }
        );

    // If user hasn't toggled a specific chart, default to "net worth" if
    // they haven't invested in goals and do have FEs; otherwise, default to
    // "in plenty".
    const hasFEs = !!fes.data?.financialEntities?.length;
    const totalGoals = sum(fes.data?.goals ?? [], getGoalBalance);
    const defaultChartType = !totalGoals && hasFEs ? 'net-worth' : 'in-plenty';

    return (
        <Ctx.Provider
            value={{
                chartType: chartType ?? defaultChartType,
                setChartType,
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

export const useDashboardContext = (): DashboardContext => {
    return useContext(Ctx);
};
